import { useEffect, useRef, useState } from "react";
import type { DeskLoginConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

/**
 * Episode 4 ("Secure sign-in") — the workstation's lock screen. Reuses the
 * exact same Windows-11-style lock-screen visual (`.cf-win-lock` etc.) that
 * {@link PcScreenOverlay} already built for the "walk up to the desk PC"
 * flow, but rendered the way every OTHER 3D course signs a learner in: as an
 * ordinary monitor-blended SCREEN_KINDS device behind the power-on gate
 * (see `SCREEN_KINDS` in App.tsx), not a full-room-dimming decision card.
 * The learner actually types (or copies from the sticky-note card pinned to
 * the top-right corner of the screen) the exact password and clicks the
 * arrow to sign in — a wrong password shakes and asks to try again, exactly
 * like a real lock screen, so this is a genuine small interaction rather
 * than a rubber-stamp "always succeeds" click-through.
 */
export function DeskLogin({
  config,
  onSubmit,
}: {
  config: DeskLoginConfig;
  onSubmit: (fields: { email: string; password: string }) => void;
}) {
  const started = useRef(false);
  const [typed, setTyped] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [wrong, setWrong] = useState(false);
  const [copied, setCopied] = useState(false);
  // The screen-power gate no longer forces the learner through a separate
  // "click the sticky note" hotspot (2026-07-25 feedback) — once the screen
  // is on, THIS blinking frame around the password field is the one and only
  // "click here" cue. It clears itself the moment the learner actually
  // engages with the field (focus, type, or use the copy-password note).
  const [interacted, setInteracted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    audio.logon();
  }, []);

  const cred = config.credential;
  const expectedPassword = cred?.password ?? "";

  const copyPassword = async () => {
    if (!cred) return;
    try {
      await navigator.clipboard?.writeText(cred.password);
    } catch {
      /* clipboard permissions vary; the note is still readable to retype */
    }
    setTyped(cred.password);
    setWrong(false);
    setCopied(true);
    setInteracted(true);
    window.setTimeout(() => setCopied(false), 1400);
    audio.click();
    inputRef.current?.focus();
  };

  const submit = () => {
    if (!expectedPassword || typed === expectedPassword) {
      audio.logon();
      onSubmit({ email: cred?.email ?? "", password: typed || expectedPassword });
    } else {
      audio.errorBuzz();
      setWrong(true);
      setTyped("");
      inputRef.current?.focus();
    }
  };

  return (
    <div className="win desklock">
      <div className="cf-win-lock">
        <div className={`cf-win-login ${wrong ? "cf-win-login--shake" : ""}`}>
          <div className="cf-win-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="54" height="54">
              <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="rgba(255,255,255,.9)" />
            </svg>
          </div>
          <div className="cf-win-user">{config.userName ?? tx("A. Morgan")}</div>
          <form
            className="cf-win-pwrow"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className={`cf-win-pwfield ${interacted ? "" : "cf-blink-strong"}`}>
              <input
                ref={inputRef}
                // Deliberately type="text" + CSS masking (never a real
                // type="password" field) so Chromium never classifies this
                // as a credential field and never pops its native "Save
                // password?" bubble over the 3D scene — see the .cf-mask
                // comment in styles.css. This mirrors every other simulated
                // login in the course (PhishingLogin, WifiKiosk, PublicPc).
                type="text"
                className={revealed ? "" : "cf-mask"}
                value={typed}
                placeholder={tx("Click here to sign in")}
                aria-label={tx("Password")}
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                spellCheck={false}
                onFocus={() => setInteracted(true)}
                onChange={(e) => {
                  setTyped(e.target.value);
                  setWrong(false);
                  setInteracted(true);
                }}
              />
              <button
                type="button"
                className="cf-win-eye"
                aria-label={revealed ? tx("Hide password") : tx("Show password")}
                onClick={() => {
                  setRevealed((v) => !v);
                  inputRef.current?.focus();
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M12 5c-5 0-9 4.5-10 7 1 2.5 5 7 10 7s9-4.5 10-7c-1-2.5-5-7-10-7z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                </svg>
              </button>
            </div>
            <button type="submit" className="cf-win-go desklogin-submit" aria-label={config.continueLabel ?? tx("Sign in")}>
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  d="M5 12h13m-6-6 6 6-6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>
          {wrong ? (
            <div className="cf-win-error">{tx("The password is incorrect. Try again.")}</div>
          ) : (
            <div className="cf-win-subtle">{config.screenTitle ?? tx("NorthBay Financial — locked")}</div>
          )}
        </div>

        <div className="cf-win-lockbar" aria-hidden="true">
          <span title={tx("Network")}>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M2 9c5.5-5.5 14.5-5.5 20 0M5.5 12.5c3.6-3.6 9.4-3.6 13 0M9 16a4.2 4.2 0 0 1 6 0M12 19.5h.01"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span title={tx("Power")}>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path d="M12 3v8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M7 6a7.4 7.4 0 1 0 10 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
        </div>

        {/* The real credential, pinned to the top-right corner of the screen
            exactly where the learner is told to look — clicking copies it
            straight into the password field, matching "copy/paste the
            login" rather than having to retype it by hand. */}
        {cred && (
          <button type="button" className="desklogin-note" onClick={copyPassword}>
            <div className="desklogin-note-head">
              <span aria-hidden>{"\u{1F4CC}"}</span> {cred.source ?? tx("Sticky note on the monitor")}
            </div>
            <div className="desklogin-note-row">
              <span className="desklogin-note-k">{tx("User")}</span>
              <span className="desklogin-note-v">{cred.email}</span>
            </div>
            <div className="desklogin-note-row">
              <span className="desklogin-note-k">{tx("Pass")}</span>
              <span className="desklogin-note-v">{cred.password}</span>
            </div>
            <div className="desklogin-note-copy">{copied ? tx("Copied ✓") : tx("Click to copy password")}</div>
          </button>
        )}

        {config.hint && <div className="desklogin-hint">{config.hint}</div>}
      </div>
    </div>
  );
}
